import { useQuery } from '@tanstack/react-query';
import { getQuizQuestions, type QuizQuestion } from '@/lib/services/questions';
import { useAuthId } from '@/hooks/useAuthId';
import { useActiveRunIdentity } from '@/lib/auth/activeRunIdentity';

export function getQuizQuestionsQueryKey(
  quizId: string | undefined,
  editionId: string | undefined,
  userId: string | null
): (string | undefined | null)[] {
  return ['quizQuestions', quizId, editionId, userId];
}

export function useQuizQuestionsQuery(
  quizId?: string,
  editionId?: string,
  options?: { initialData?: QuizQuestion[]; enabled?: boolean }
) {
  const { userId, isAuthenticated, isResolved } = useAuthId();
  const activeRunIdentity = useActiveRunIdentity();
  const runtimeFetchEnabled = options?.enabled !== false;
  // Progress and the run must use the same prepared principal. The raw guest
  // ID can still be terminal, or can rotate before preparation has completed.
  const queryIdentity =
    isResolved &&
    activeRunIdentity.status === 'ready' &&
    activeRunIdentity.identity?.userId === userId &&
    activeRunIdentity.identity.isAnonymous === !isAuthenticated
      ? activeRunIdentity.identity
      : null;
  const isQueryEnabled = runtimeFetchEnabled && !!queryIdentity && !!editionId;

  const query = useQuery<QuizQuestion[]>({
    queryKey: getQuizQuestionsQueryKey(quizId, editionId, queryIdentity?.userId ?? null),
    queryFn: () => {
      if (!queryIdentity) throw new Error('The game identity is not ready. Please retry.');
      return getQuizQuestions(editionId!, queryIdentity.userId, queryIdentity.isAnonymous);
    },
    enabled: isQueryEnabled,
    placeholderData: options?.initialData,
    staleTime: 0,
    // Bootstrap questions are placeholders. Once the identity-scoped result is
    // in the cache, a second observer must not immediately repeat the same RPC.
    refetchOnMount: (cachedQuery) => cachedQuery.state.dataUpdateCount === 0,
    refetchOnWindowFocus: false,
    retry: false,
  });

  return {
    ...query,
    // Initial public questions are only placeholders (`dataUpdatedAt === 0`).
    // `isFetched` belongs to the shared query cache, unlike
    // `isFetchedAfterMount`, so the second observer sees the first real result.
    // A prior successful snapshot remains ready if a later background refresh
    // fails; the completed surface must not return to a skeleton.
    isProgressReady:
      !runtimeFetchEnabled ||
      (isQueryEnabled && query.isFetched && (query.isSuccess || query.dataUpdatedAt > 0)),
    isProgressError:
      isQueryEnabled && query.isFetched && query.isError && query.dataUpdatedAt === 0,
  };
}
